import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Calendar, MapPin, Clock } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["All", "Sonoma", "Napa", "Marin", "Lake", "Mendocino"];
const eventTypes = ["All", "Career Fair", "Volunteer", "Workshop", "Networking"];

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedEventType, setSelectedEventType] = useState("All");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, selectedCounty, selectedEventType]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch events", error);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(
        (event) =>
          event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCounty !== "All") {
      filtered = filtered.filter((event) => event.county === selectedCounty);
    }

    if (selectedEventType !== "All") {
      filtered = filtered.filter((event) => event.event_type === selectedEventType);
    }

    setFilteredEvents(filtered);
  };

  const getEventTypeColor = (type) => {
    const colors = {
      "Career Fair": "bg-blue-100 text-blue-700",
      "Volunteer": "bg-green-100 text-green-700",
      "Workshop": "bg-purple-100 text-purple-700",
      "Networking": "bg-orange-100 text-orange-700"
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
          Events & Opportunities
        </h1>
        <p className="text-gray-600">Discover community events and volunteer opportunities</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="event-search-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select value={selectedCounty} onValueChange={setSelectedCounty}>
            <SelectTrigger data-testid="event-county-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {counties.map((c) => (
                <SelectItem key={c} value={c}>{c} County</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedEventType} onValueChange={setSelectedEventType}>
            <SelectTrigger data-testid="event-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {eventTypes.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading events...</div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No events found matching your criteria</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => (
            <Card
              key={event.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4"
              style={{ borderLeftColor: '#4ECDC4' }}
              onClick={() => navigate(`/events/${event.id}`)}
              data-testid={`event-card-${event.id}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                      {event.name}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {event.description.length > 120 ? `${event.description.substring(0, 120)}...` : event.description}
                    </CardDescription>
                  </div>
                  <Badge className={getEventTypeColor(event.event_type)} data-testid="event-type-badge">
                    {event.event_type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{event.city}, {event.county} County</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#FF6B6B' }}>
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(event.date), "MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{event.time}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}